import { CheckCircle2, XCircle, CheckSquare, Ban, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface BulkActionBarProps {
  selectedCount: number;
  totalCount: number;
  pendingCount: number;
  onAcceptSelected: () => void;
  onRejectSelected: () => void;
  onAcceptAboveThreshold: (threshold: number) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  isProcessing?: boolean;
  className?: string;
}

export function BulkActionBar({
  selectedCount,
  totalCount,
  pendingCount,
  onAcceptSelected,
  onRejectSelected,
  onAcceptAboveThreshold,
  onSelectAll,
  onClearSelection,
  isProcessing = false,
  className,
}: BulkActionBarProps) {
  const hasSelection = selectedCount > 0;
  const allSelected = selectedCount === totalCount && totalCount > 0;

  // No selection: show quick tier-based actions
  if (!hasSelection) {
    return (
      <Card className={cn('', className)}>
        <CardContent className="py-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={onSelectAll}
                disabled={totalCount === 0}
              >
                <CheckSquare className="h-4 w-4 mr-1.5" />
                Select All
              </Button>

              <div className="h-6 w-px bg-border mx-1" />

              <span className="text-sm font-medium text-foreground">Quick Actions:</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onAcceptAboveThreshold(90)}
                disabled={isProcessing}
                className="bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                Accept all matched answers
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onAcceptAboveThreshold(85)}
                disabled={isProcessing}
                className="bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                Accept matched + close matches
              </Button>
            </div>

            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{pendingCount}</span> of{' '}
              {totalCount} pending review
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sticky bar when items are selected
  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-t shadow-lg',
        className
      )}
    >
      <div className="container mx-auto py-3 px-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-primary/10 text-primary px-3 py-1.5 rounded-md">
              <CheckSquare className="h-4 w-4" />
              <span className="font-medium">{selectedCount} selected</span>
            </div>

            <Button
              variant="default"
              size="sm"
              onClick={onAcceptSelected}
              disabled={isProcessing}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle2 className="h-4 w-4 mr-1.5" />
              Use these answers
            </Button>

            <Button
              variant="destructive"
              size="sm"
              onClick={onRejectSelected}
              disabled={isProcessing}
            >
              <Ban className="h-4 w-4 mr-1.5" />
              Skip all
            </Button>
          </div>

          <div className="flex items-center gap-2">
            {allSelected ? (
              <Button variant="ghost" size="sm" onClick={onClearSelection}>
                <XCircle className="h-4 w-4 mr-1.5" />
                Deselect All
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={onSelectAll}>
                  Select All ({totalCount})
                </Button>
                <Button variant="ghost" size="sm" onClick={onClearSelection}>
                  <X className="h-4 w-4 mr-1.5" />
                  Clear
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
